// This is the only file outside useAuthStore.ts permitted to import axios directly.
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { ApiError, LegalReacceptDeclined } from "@/lib/apiError";
import { generateRequestId } from "@/lib/requestId";
import { clientTimezone } from "@/lib/clientTimezone";
import { detectHost } from "@/lib/host";
import { spaNavigate } from "@/lib/navigation";
import { useAuthStore } from "@/stores/useAuthStore";
import { useModalStore } from "@/stores/useModalStore";

// Single in-flight refresh promise. When non-null, new 401-triggered refreshes
// await this instead of issuing a second POST /auth/refresh.
let inflightRefresh: Promise<string> | null = null;

// Marker for "this request is already a retry". Must be on the config object
// so it travels with the AxiosRequestConfig that axios passes back on retry.
type ConfigWithMeta = InternalAxiosRequestConfig & {
  _isRetry?: boolean;
  _legalReacceptHandled?: boolean;
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ── Request interceptor ────────────────────────────────────────────────────────

apiClient.interceptors.request.use((config: ConfigWithMeta) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  if (!config.headers["X-Request-ID"]) {
    config.headers["X-Request-ID"] = generateRequestId();
  }
  return config;
});

// ── Response interceptor ───────────────────────────────────────────────────────

apiClient.interceptors.response.use(
  async (response) => {
    // Success path: check for X-Legal-Reaccept-Required header.
    const reaccept = response.headers["x-legal-reaccept-required"] as string | undefined;
    if (reaccept === "terms" || reaccept === "privacy") {
      const config = response.config as ConfigWithMeta;
      if (!config._legalReacceptHandled) {
        config._legalReacceptHandled = true;
        await ensureLegalReaccept(reaccept);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    return handleError(error);
  }
);

// ── Envelope parser (F-17-S-2) — handles three backend response shapes ─────────
//
// Shape 1: {"detail": {"error": ..., "message": ..., "details": ...}}  — _err() callers
// Shape 2: {"detail": "some_code"}                                      — deps.py plain-string
// Shape 3: {"error": ..., "message": ..., "details": ...}               — rate-limit handler

function parseEnvelope(error: AxiosError): {
  code: string;
  message: string;
  details: Record<string, unknown>;
  requestId: string | undefined;
} {
  const data = error.response?.data;
  let code = "network_error";
  let message = error.message;
  let details: Record<string, unknown> = {};

  if (data && typeof data === "object") {
    const root = data as Record<string, unknown>;
    const detail = root["detail"];
    if (typeof detail === "string") {
      // Shape 2: plain-string detail
      code = detail;
    } else if (detail && typeof detail === "object") {
      // Shape 1: nested detail object
      const d = detail as Record<string, unknown>;
      if (typeof d["error"] === "string") code = d["error"];
      if (typeof d["message"] === "string") message = d["message"];
      if (d["details"] && typeof d["details"] === "object")
        details = d["details"] as Record<string, unknown>;
    } else {
      // Shape 3: flat envelope (rate-limit handler)
      if (typeof root["error"] === "string") code = root["error"];
      if (typeof root["message"] === "string") message = root["message"];
      if (root["details"] && typeof root["details"] === "object")
        details = root["details"] as Record<string, unknown>;
    }
  }

  // request_id always from response header (set by RequestIDMiddleware), with
  // fallback to the outbound request header.
  const requestId =
    (error.response?.headers?.["x-request-id"] as string | undefined) ??
    (error.config?.headers?.["X-Request-ID"] as string | undefined);

  return { code, message, details, requestId };
}

// ── Error handler — the state machine ─────────────────────────────────────────

async function handleError(error: AxiosError): Promise<never> {
  const config = error.config as ConfigWithMeta | undefined;
  const status = error.response?.status ?? 0;
  const { code, message, details, requestId } = parseEnvelope(error);

  // ── WS-E / invariant #9 — global temp-password interception ────────────────
  // The backend ``PasswordResetGateMiddleware`` returns 403 with body
  // ``{"error": "password_change_required", ...}`` on EVERY authed request
  // (parsed here as ``code``) while ``users.requires_password_reset = TRUE``,
  // EXCEPT the backend-allowlisted change-password/logout suffixes. Because
  // this is the single global response-error handler for ``apiClient``, this
  // ONE check covers all authed routes — no per-page wiring (invariant #9).
  // It redirects the SPA to the forced-change-password route, where the user
  // can rotate the temp credential and clear the flag.
  if (status === 403 && code === "password_change_required") {
    redirectToForcedPasswordChange();
    // Still throw so the originating caller's promise rejects (the navigation
    // tears down the page; the rejection prevents downstream success handlers
    // from acting on a request the gate refused).
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Non-401 → just throw.
  if (status !== 401) {
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Codes that force immediate logout on a 401 against any protected endpoint.
  // Defensive set — some codes are not emitted by today's backend but are kept
  // to harden against future backend additions. Rationale in batch_17_security.md F-17-S-3.
  const immediateLogoutCodes = new Set([
    "impersonation_expired",    // emitted by app/db/session.py on expired imp
    "tenant_suspended",         // only emitted on login today; mid-session gap P1-13
    "tenant_pending_deletion",  // only emitted on login today; mid-session gap P1-13
    "session_terminated",       // emitted on refresh-theft; kept defensive for protected paths
    // REMOVED: "session_revoked" — not emitted anywhere; was a spec-doc phantom.
  ]);

  if (immediateLogoutCodes.has(code)) {
    await forceLogoutAndRedirect();
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // If the retryable 401 came from /auth/refresh itself, that's a terminal failure.
  // Strict equality (not endsWith) per F-17-S-7.
  if (config?.url === "/auth/refresh") {
    await forceLogoutAndRedirect();
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Anti-loop: if this IS the retry and we still got 401, don't re-enter refresh.
  if (config?._isRetry) {
    await forceLogoutAndRedirect();
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Backend emits `invalid_token` for every 401 from AuthMiddleware / deps.py —
  // expired, malformed, missing header, deleted-user-row all collapse to this
  // one code. Refresh-then-retry is safe: if the underlying cause is NOT
  // expiry (e.g. user row deleted), POST /auth/refresh will itself return 401,
  // which hits the url === "/auth/refresh" branch → forceLogoutAndRedirect.
  // No looping risk; at worst one wasted refresh call per truly-invalid token.
  // Backend-hygiene ticket P1-11 will split this into distinct codes later.
  const refreshableCodes = new Set(["invalid_token"]);

  if (!refreshableCodes.has(code)) {
    // Unknown 401 code — throw rather than silently refresh.
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Belt: short-circuit if user is already anonymous (F-17-S-5).
  if (useAuthStore.getState().status === "anonymous") {
    throw new ApiError({ error: code, message, details, requestId, status });
  }

  // Silent-refresh path.
  try {
    const newToken = await ensureRefresh();
    if (!config) {
      throw new ApiError({ error: code, message, details, requestId, status });
    }
    const retryHeaders = config.headers.toJSON(true) as Record<string, string>;
    retryHeaders["Authorization"] = `Bearer ${newToken}`;
    const retryConfig: ConfigWithMeta = {
      ...config,
      _isRetry: true,
      headers: retryHeaders as typeof config.headers,
    };
    return (await apiClient.request(retryConfig)) as never;
  } catch {
    // Refresh failed — logout + redirect already triggered inside ensureRefresh.
    throw new ApiError({
      error: "session_refresh_failed",
      message: "Your session expired. Sign in again.",
      details: {},
      requestId,
      status: 401,
    });
  }
}

// ── ensureRefresh — single in-flight promise (F-17-S-5: both guards) ──────────

type RefreshResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

async function ensureRefresh(): Promise<string> {
  // Belt: if user is already anonymous, don't even start the refresh.
  if (useAuthStore.getState().status === "anonymous") {
    throw new Error("session_already_anonymous");
  }
  if (inflightRefresh !== null) {
    return await inflightRefresh;
  }
  inflightRefresh = (async () => {
    try {
      const resp = await apiClient.post<RefreshResponse>("/auth/refresh", {});
      const token = resp.data.access_token;
      // Suspenders: re-check status after the network round-trip. If the user
      // clicked Logout mid-flight, discard the new token and throw — queued
      // retries fall into the force-logout branch instead of using a token
      // against a session the user just ended.
      if (useAuthStore.getState().status === "anonymous") {
        throw new Error("session_ended_during_refresh");
      }
      useAuthStore.getState().setAccessToken(token);
      useAuthStore.getState().hydrateFromAccessToken(token);
      return token;
    } catch (err) {
      await forceLogoutAndRedirect();
      throw err;
    } finally {
      inflightRefresh = null;
    }
  })();
  return await inflightRefresh;
}

// ── handleLegalReaccept ────────────────────────────────────────────────────────

// Concurrency guard for the re-accept flow. A page fires several API calls at
// once, and every response can carry the reaccept header. Without this, each
// caller independently opens the single-slot blocking modal — the second
// open() overwrites the first in the modal store, orphaning the first caller's
// promise so its query hangs on "Loading…" forever. Sharing ONE in-flight
// reaccept cycle across all concurrent callers makes them resolve together.
let reacceptInFlight: Promise<void> | null = null;

async function ensureLegalReaccept(flavor: "terms" | "privacy"): Promise<void> {
  if (!reacceptInFlight) {
    reacceptInFlight = handleLegalReaccept(flavor).finally(() => {
      reacceptInFlight = null;
    });
  }
  await reacceptInFlight;
}

async function handleLegalReaccept(flavor: "terms" | "privacy"): Promise<void> {
  const { open } = useModalStore.getState();
  // Modal is blocking for legal-reaccept types (Modal.tsx) — only "confirmed" can resolve.
  // We loop on POST failure so a transient network blip doesn't strand the user.
  for (;;) {
    const result = await open({
      type: flavor === "privacy" ? "privacy-reaccept" : "terms-reaccept",
      props: {},
    });
    if (result !== "confirmed") {
      // Should not happen with the blocking modal, but defend just in case
      // some future modal change reintroduces a dismiss path.
      throw new LegalReacceptDeclined(flavor);
    }
    try {
      await apiClient.post(
        "/auth/accept-legal",
        // `affirmative: true` is the server-enforced "I agree" — the backend
        // rejects the request without it. clientTimezone() adds the same
        // evidence captured at signup.
        { flavor, affirmative: true, ...clientTimezone() },
        // Mark so the response interceptor doesn't double-handle if the POST
        // somehow comes back with the header (it shouldn't — /auth/* is skipped
        // by LegalVersionGateMiddleware — but belt-and-suspenders).
        { _legalReacceptHandled: true } as unknown as ConfigWithMeta,
      );
      return; // success
    } catch {
      // Re-open modal so user can retry. Loop.
      continue;
    }
  }
}

// ── redirectToForcedPasswordChange (WS-E / invariant #9) ───────────────────────
//
// Host-aware target for the forced temp-password rotation route:
//   - app host:   /<tenantSlug>/change-password   (tenant users)
//   - admin host: /admin/change-password           (super-admins)
// The change-password endpoint itself is backend-allowlisted, so a flagged user
// CAN reach this route without re-triggering the 403 — but we still guard
// against redirecting when already on the change-password route so a stray 403
// from some in-page background request can't cause a reload loop.
//
// SPA navigation (NOT window.location.assign): a full page reload wipes the
// in-memory access token, and a flagged principal cannot recover it via
// /auth/refresh (RB-19a blocks refresh while requires_password_reset = TRUE).
// The change-password POST would then go out with no Authorization header and
// 401. spaNavigate preserves the in-memory token through the redirect.
function redirectToForcedPasswordChange(): void {
  if (typeof window === "undefined") return;

  // Tenant-slug resolution: prefer the store, but the DIRECT tenant-login path
  // deliberately mints an access token with tslug=null (sessions.py
  // `_tenant_slug_for` — the `_UserStub` carries no `tenant` relationship, so
  // slug routing is expected to fall back to the URL segment). When that 403
  // fires the user is on `/<slug>/dashboard` (or `/<slug>/login`), so derive the
  // slug from the first non-empty path segment, excluding reserved roots that
  // are never a tenant slug.
  const storeSlug = useAuthStore.getState().tenantSlug;
  let slug = storeSlug ?? "";
  if (!slug) {
    const first = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
    if (first && !["login", "admin", "change-password", "dashboard"].includes(first)) {
      slug = first;
    }
  }
  const target =
    detectHost() === "admin"
      ? "/admin/change-password"
      : slug
        ? `/${slug}/change-password`
        : "/login";

  // Already there (or a sub-path of it) → do nothing. Prevents redirect loops.
  if (window.location.pathname === target) return;

  spaNavigate(target);
}

// ── forceLogoutAndRedirect ─────────────────────────────────────────────────────

async function forceLogoutAndRedirect(): Promise<void> {
  const { tenantSlug } = useAuthStore.getState();
  useAuthStore.getState().reset();
  // Best-effort server-side logout. Don't await the 401; it's already happened.
  try {
    await apiClient.post("/auth/logout");
  } catch {
    // Swallow — we're tearing down regardless.
  }
  const target = tenantSlug ? `/${tenantSlug}/login` : "/login";
  if (typeof window !== "undefined" && window.location.pathname !== target) {
    window.location.assign(target);
  }
}
